import React, { useEffect, useState } from "react"
import { injectIntl } from "react-intl"
import { useMutation, useQuery } from "react-query"
import GlobalHelper from "../../../common/GlobalHelper"
import { useFetchLabels } from "../../../common/hooks/UseFetchLabels"
import GenericListingTable from "../../../common/listing/generic-listing/listing-table/ListingTable"
import { getDefaultSortId, getDefaultView, getObjectTypeConfig } from "../../../common/listing/generic-listing/utils"
import Pagination from "../../../common/listing/pagination.component"
import Search from "../../../common/search-filter/search/search"
import LoadingSpinner from "../../../common/spinner/loading.component"
import { Filter, SearchPreference } from "../../../payment/listing/types"
import IntlProps from "../../../state/helpers/IntlProps"
import ListingHelper from "../../../state/listing/ListingHelper"
import { PREFERENCE_TYPE } from "../../../state/listing/constants"
import { FILTERS_DEFAULTS, ObjectType, ObjectTypeConfig, PAGINATION_DEFAULTS, SORTING_DEFAULTS, View } from "../../../state/listing/generic-listing"
import { StockDocumentListingService } from "../../../state/listing/inventory/service/service"
import { SearchData } from "../../../state/search/SearchService"
import SearchTotals from "../../../state/search/SearchTotals"
import { StockLocationTab } from "../../components"
import { ControlPanel } from "./ControlPanel"
import { ReorderBanner } from "./ReorderBanner"

export const StockDocumentListing = injectIntl((props:IntlProps) => {   
    const [paginatedSearch, setPaginatedSearch] = useState<SearchPreference>({
        activePage: PAGINATION_DEFAULTS.activePage,
        pageSize: PAGINATION_DEFAULTS.pageSize,
        sortByField: getDefaultSortId(ObjectType.INVENTORY_STOCK_DOCUMENTS),
        sortAsc: SORTING_DEFAULTS.asc,
        keywords: FILTERS_DEFAULTS.keywords,
        filters: FILTERS_DEFAULTS.filters,
        view: getDefaultView(ObjectType.INVENTORY_STOCK_DOCUMENTS),
    } as SearchPreference)
    const [activeTab, setActiveTab] = useState<string | null>(null)
    const controller = new AbortController()
    const [config, setConfig] = useState<ObjectTypeConfig>()
    const { labelOptions, loadingLabelOptions } = useFetchLabels(paginatedSearch.view, controller)

    useEffect(() => {
        const config = getObjectTypeConfig(ObjectType.INVENTORY_STOCK_DOCUMENTS)
        setConfig(config)
        
        return () => controller.abort()
    }, [])

    const { isFetchedAfterMount: isPreferenceFetched } = useQuery(
        ["StockDocumentListingService.getListingPreferences"], () => {
            return GlobalHelper.getListingPreferences(PREFERENCE_TYPE.LISTING_STOCK_DOCUMENTS, false)
        }, 
        {
            onSuccess: preferences => {
                updatePaginatedSearch({
                    ...paginatedSearch,
                    ...preferences,
                }, false)
            }
        })

    const queryKey = ["stock-document-listing", paginatedSearch, activeTab]
    const { data, isLoading, isFetched } = useQuery(queryKey, () => {
        return StockDocumentListingService.findStockDocuments({
            ...paginatedSearch,
            additionalFilters: {
                inventoryLocationId: activeTab || ""
            },
        })
    },
    {
        enabled: !!activeTab && isPreferenceFetched,
    })

    const totalsQueryKey = ["stock-document-listing-totals", paginatedSearch, activeTab]
    const {data: totals} = useQuery(totalsQueryKey, () => {
        return StockDocumentListingService.findStockDocumentsTotals({
            ...paginatedSearch,
            additionalFilters: {
                inventoryLocationId: activeTab || ""
            },
            totals: ["totalAmount"]
        })
    },
    {
        enabled: !!activeTab && isPreferenceFetched,
    })

    const { mutate: savePreferences } = useMutation((paginatedSearch: SearchPreference) => {
        return ListingHelper.saveListingPreferences(PREFERENCE_TYPE.LISTING_STOCK_DOCUMENTS, paginatedSearch)
    })

    const updatePaginatedSearch = ((newState: Partial<SearchPreference>, shouldSavePreferences = true) => {
        setPaginatedSearch((curr) => ({ ...curr, ...newState }))
        shouldSavePreferences && savePreferences({ ...paginatedSearch, ...newState } as SearchPreference)
    })


    const handlePaginationChange = (activePage: number) => {
        updatePaginatedSearch({ activePage })
    }

    const handlePageSizeChange = (pageSize: number) => {
        const updatedPaginatedSearch: Partial<SearchPreference> = { pageSize }
        let count=0
        if (totals instanceof SearchTotals) {
            count = totals.getTotalItemsCount() 
        }

        if (pageSize * paginatedSearch.activePage >count ) {
            updatedPaginatedSearch.activePage = 1
        }

        updatePaginatedSearch(updatedPaginatedSearch)
    }

    const refreshSearch = (
        renderedKeywords: string,
        renderedFilters: { [key: string]: Filter },
        savedFilterId: string,
        _: SearchData,
        view: View,
    ) => {
        updatePaginatedSearch({
            activePage: 1,
            filters: renderedFilters,
            keywords: renderedKeywords,
            savedFilterId,
            view: {
                ...view,
                columns: view.columns.length ? view.columns : getDefaultView(ObjectType.INVENTORY_STOCK_DOCUMENTS).columns
            }
        }, isPreferenceFetched)
    }
    
    const handleLocationChange = (locationId: string | null) => {
        if (locationId && locationId !== activeTab) {
            setActiveTab(locationId)
            setPaginatedSearch({...paginatedSearch,activePage:1})
        }
    }

    const handleApplyView = (view: View) => {
        updatePaginatedSearch({ view })
        savePreferences({
            ...paginatedSearch,
            view,
        })
    }

    const handleSortingChange = (sortField: string, asc: boolean) => {
        updatePaginatedSearch({
            sortByField: sortField,
            sortAsc: asc,
        })
    }

    return (
        <>
            {!config && (
                <LoadingSpinner />
            )}
            {config && (
                <>
                    <div className="pb-2">
                        {isPreferenceFetched && (
                            <Search
                                typeOfSearch={ObjectType.INVENTORY_STOCK_DOCUMENTS}
                                searchKeywords={paginatedSearch.keywords}
                                filtersToUse={paginatedSearch.filters}
                                object={[config.searchObjectType]}
                                placeholder={props.intl.formatMessage(config.searchPlaceholder)}
                                savedFilterId={paginatedSearch.savedFilterId}
                                view={paginatedSearch.view}
                                onChange={refreshSearch}
                            >
                                <ControlPanel />
                            </Search>
                        )}
                    </div>
                    <ReorderBanner />
                    <StockLocationTab activeTab={activeTab} onTabChangeCallback={handleLocationChange}/>
                    <GenericListingTable
                        config={config}
                        data={data?.data || []}
                        totals={totals}
                        totalItemsCount={data?.metadata.totalItemsCount || 0}
                        view={paginatedSearch.view}
                        sorting={{
                            sortField: paginatedSearch.sortByField,
                            asc: paginatedSearch.sortAsc,
                        }}
                        selectableRows={false}
                        additionalOptions={{
                            labelOptions: labelOptions || [],
                        }}
                        loadingState={{
                            loadingData: isLoading || !isFetched,
                            loadingLabelOptions,
                        }}
                        onSortingChange={handleSortingChange}
                        onOpenBulkModal={() => null}
                        onApplyView={handleApplyView}
                    />
                    <Pagination
                        className="px-2"
                        activePage={paginatedSearch.activePage}
                        pageSize={paginatedSearch.pageSize}
                        showPageSize={true}
                        totalItemsCount={data?.metadata.totalItemsCount || 0}
                        onChange={handlePaginationChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </>
            )}
        </>
    )
})


// WEBPACK FOOTER //
// ./src/inventory/sections/control/StockDocumentListing.tsx